import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useMeta from "@/hooks/useMeta";
import TaskModal from "@/components/TaskModal";
import PhaseModal from "@/components/PhaseModal";
import {
  Card, Button, Select, Modal, Spinner, ProgressBar, StatusBadge, PriorityBadge, WeightBadge, TimelineBadge, Avatar, EmptyState, usePagination, Pagination,
} from "@/components/UI";
import { STATUS_META, TIMELINE_STATUS_META } from "@/lib/constants";
import { ArrowLeft, Plus, Download, GanttChartSquare, AlertTriangle, Pencil, Trash2, Layers, ArrowRightCircle, LayoutList } from "lucide-react";

const DAY_MS = 24 * 60 * 60 * 1000;
const DAY_WIDTH = 26;
const PHASE_COL_WIDTH = 130;
const TASK_COL_WIDTH = 200;
const PHASE_COLORS = ["#5d5f61", "#989498", "#475754", "#5d5f62", "#858e8d"];
const UNPHASED_COLOR = "#475754";

function parseDate(v) {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d) ? null : d;
}
function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function daysBetween(a, b) {
  return Math.round((startOfDay(b) - startOfDay(a)) / DAY_MS);
}
function addDays(d, n) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

const STAT_ORDER = ["backlog", "to_do", "in_progress", "review", "done"];

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const meta = useMeta();
  const [project, setProject] = useState(null);
  const [modal, setModal] = useState({ open: false, mode: "view", task: null });
  const [phaseModal, setPhaseModal] = useState({ open: false, phase: null });
  const [deletePhaseTarget, setDeletePhaseTarget] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [selectedBacklog, setSelectedBacklog] = useState([]);
  const [targetSprint, setTargetSprint] = useState("");
  const [delivering, setDelivering] = useState(false);
  const [ganttMode, setGanttMode] = useState("workflow");

  const load = useCallback(() => api.get(`/projects/${id}`).then((r) => setProject(r.data)).catch(() => toast.error("Gagal memuat project")), [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const activeSprint = meta.sprints.find((s) => s.status === "active");
    if (activeSprint && !targetSprint) setTargetSprint(String(activeSprint.id));
  }, [meta.sprints, targetSprint]);

  const groups = useMemo(() => {
    if (!project) return [];
    const tasks = (project.tasks || []).filter((t) => !["cancelled", "replaced"].includes(t.status));
    const sortTasks = (list) =>
      [...list].sort((a, b) => {
        const da = parseDate(a.start_date) || parseDate(a.deadline) || new Date(a.created_at);
        const db = parseDate(b.start_date) || parseDate(b.deadline) || new Date(b.created_at);
        return da - db;
      });

    const list = (project.phases || []).map((p, idx) => ({
      id: p.id,
      name: p.name,
      color: PHASE_COLORS[idx % PHASE_COLORS.length],
      tasks: sortTasks(tasks.filter((t) => t.phase_id === p.id)),
      deletable: true,
    }));
    const unphased = sortTasks(tasks.filter((t) => !t.phase_id));
    if (unphased.length > 0) {
      list.push({ id: "unphased", name: "Belum Ada Tahapan", color: UNPHASED_COLOR, tasks: unphased, deletable: false });
    }
    return list;
  }, [project]);

  const allVisibleTasks = useMemo(() => groups.flatMap((g) => g.tasks), [groups]);
  const backlogTasks = useMemo(() => allVisibleTasks.filter((t) => t.status === "backlog"), [allVisibleTasks]);
  const deliveredTasks = useMemo(() => allVisibleTasks.filter((t) => t.status !== "backlog"), [allVisibleTasks]);
  const pgBacklog = usePagination(backlogTasks, 10);
  const pgDelivered = usePagination(deliveredTasks, 10);

  const { rangeStart, rangeEnd } = useMemo(() => {
    const dates = [];
    allVisibleTasks.forEach((t) => {
      const s = parseDate(t.start_date);
      const d = parseDate(t.deadline);
      if (s) dates.push(s);
      if (d) dates.push(d);
    });
    if (project?.start_date) dates.push(parseDate(project.start_date));
    if (project?.end_date) dates.push(parseDate(project.end_date));
    const today = new Date();
    if (dates.length === 0) return { rangeStart: addDays(today, -3), rangeEnd: addDays(today, 21) };
    const min = new Date(Math.min(...dates));
    const max = new Date(Math.max(...dates));
    return { rangeStart: addDays(min, -2), rangeEnd: addDays(max, 3) };
  }, [allVisibleTasks, project]);

  const totalDays = Math.max(1, daysBetween(rangeStart, rangeEnd) + 1);
  const today = startOfDay(new Date());

  const days = useMemo(() => {
    const list = [];
    for (let i = 0; i < totalDays; i++) list.push(addDays(rangeStart, i));
    return list;
  }, [rangeStart, totalDays]);

  const months = useMemo(() => {
    const list = [];
    let cursor = 0;
    while (cursor < days.length) {
      const d = days[cursor];
      const label = d.toLocaleDateString("id-ID", { month: "short", year: "numeric" });
      let count = 0;
      while (cursor < days.length && days[cursor].toLocaleDateString("id-ID", { month: "short", year: "numeric" }) === label) {
        cursor++;
        count++;
      }
      list.push({ label, count });
    }
    return list;
  }, [days]);

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const res = await api.get(`/projects/${id}/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `timeline-${project.name}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Gagal mengunduh Excel");
    } finally {
      setDownloading(false);
    }
  };

  const allBacklogSelected = backlogTasks.length > 0 && selectedBacklog.length === backlogTasks.length;
  const toggleAllBacklog = () => setSelectedBacklog(allBacklogSelected ? [] : backlogTasks.map((t) => t.id));
  const toggleBacklogTask = (taskId) =>
    setSelectedBacklog((prev) => (prev.includes(taskId) ? prev.filter((x) => x !== taskId) : [...prev, taskId]));

  const deliverToSprint = async () => {
    if (!targetSprint || selectedBacklog.length === 0) return;
    setDelivering(true);
    try {
      await api.post("/tasks/bulk-assign-sprint", { task_ids: selectedBacklog, sprint_id: Number(targetSprint) });
      toast.success(`${selectedBacklog.length} backlog dikirim ke To Do`);
      setSelectedBacklog([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal mengirim ke sprint");
    } finally {
      setDelivering(false);
    }
  };

  const openCreatePhase = () => setPhaseModal({ open: true, phase: null });
  const openEditPhase = (phase) => setPhaseModal({ open: true, phase });

  const confirmDeletePhase = async () => {
    if (!deletePhaseTarget) return;
    try {
      await api.delete(`/phases/${deletePhaseTarget.id}`);
      toast.success("Tahapan dihapus");
      setDeletePhaseTarget(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Gagal menghapus tahapan");
      setDeletePhaseTarget(null);
    }
  };

  if (!project) return <Spinner />;

  const byStatus = project.tasks_by_status || {};
  const phaseGroups = groups.filter((g) => g.deletable);
  const isCompleted = project.status === "completed";
  const canEditProject = isAdmin && !isCompleted;

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="project-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            onClick={() => navigate("/projects")}
            className="mb-2 flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300"
            data-testid="project-detail-back"
          >
            <ArrowLeft size={14} /> Kembali ke Projects
          </button>
          <div className="flex items-center gap-2">
            <span className="h-3.5 w-3.5 rounded-full" style={{ backgroundColor: project.color }} />
            <h1 className="font-heading text-3xl font-bold tracking-tight">{project.name}</h1>
            {project.is_late && project.status === "active" ? (
              <span className="rounded-md bg-rose-500/15 px-2 py-0.5 text-[11px] font-semibold text-rose-400">Terlambat</span>
            ) : (
              <StatusBadge status={project.status === "completed" ? "done" : project.status === "on_hold" ? "on_hold" : project.status === "planned" ? "planned" : "in_progress"} />
            )}
          </div>
          <p className="mt-1 text-sm text-slate-400">Susun tahapan &amp; backlog project ini.</p>
          {project.description && <p className="mt-1 max-w-2xl text-sm text-slate-400">{project.description}</p>}
          <p className="mt-1 text-xs text-slate-500">
            {project.start_date ? new Date(project.start_date).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
            {" — "}
            {project.end_date ? new Date(project.end_date).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={downloadExcel} disabled={downloading} data-testid="project-download-excel">
            <Download size={16} /> {downloading ? "Menyiapkan..." : "Download Excel"}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        <Card className="p-3">
          <p className="text-[11px] uppercase tracking-wider text-slate-500">Total Backlog</p>
          <p className="font-mono text-xl font-semibold text-slate-100">{project.total_tasks ?? 0}</p>
        </Card>
        {STAT_ORDER.map((s) => (
          <Card key={s} className="p-3">
            <p className="text-[11px] uppercase tracking-wider" style={{ color: STATUS_META[s]?.color }}>{STATUS_META[s]?.label}</p>
            <p className="font-mono text-xl font-semibold text-slate-100">{byStatus[s] ?? 0}</p>
          </Card>
        ))}
      </div>

      <Card className="p-5">
        <div className="mb-2 flex items-center justify-between text-xs">
          <span className="text-slate-400">Progress keseluruhan (bobot)</span>
          <span className="font-mono tabular-nums text-slate-400">{project.progress ?? 0}%</span>
        </div>
        <ProgressBar value={project.progress ?? 0} max={100} color={project.color} height={10} />
        {project.overdue_tasks > 0 && (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-rose-400">
            <AlertTriangle size={13} /> {project.overdue_tasks} backlog melewati deadline
          </p>
        )}
      </Card>

      {/* Phase management */}
      <Card className="p-5" data-testid="project-phases-card">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="flex items-center gap-2 font-heading text-sm font-semibold text-slate-200">
            <Layers size={15} /> Tahapan
          </h3>
          {canEditProject && (
            <Button size="sm" onClick={openCreatePhase} data-testid="add-phase-button">
              <Plus size={14} /> Tambah Tahapan
            </Button>
          )}
        </div>
        {phaseGroups.length === 0 ? (
          <p className="text-sm text-slate-500">
            Belum ada tahapan. Tambahkan tahapan (misal: Development, Testing) supaya backlog bisa dikelompokkan.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {phaseGroups.map((g) => (
              <div
                key={g.id}
                className="flex items-center gap-2 rounded-lg border border-slate-800 bg-slate-950/50 px-3 py-1.5"
                data-testid={`phase-chip-${g.id}`}
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: g.color }} />
                <span className="text-sm font-medium text-slate-200">{g.name}</span>
                <span className="text-xs text-slate-500">({g.tasks.length})</span>
                {canEditProject && (
                  <>
                    <button
                      onClick={() => setModal({ open: true, mode: "create", task: { project_id: project.id, phase_id: g.id, status: "backlog" } })}
                      className="rounded p-1 text-slate-500 hover:bg-slate-500/10 hover:text-slate-400"
                      title="Tambah task di tahapan ini"
                      data-testid={`add-task-to-phase-button-${g.id}`}
                    >
                      <Plus size={12} />
                    </button>
                    <button
                      onClick={() => openEditPhase(g)}
                      className="rounded p-1 text-slate-500 hover:bg-slate-500/10 hover:text-slate-400"
                      title="Ubah nama tahapan"
                      data-testid={`edit-phase-button-${g.id}`}
                    >
                      <Pencil size={12} />
                    </button>
                    <button
                      onClick={() => setDeletePhaseTarget(g)}
                      className="rounded p-1 text-slate-500 hover:bg-rose-500/10 hover:text-rose-400"
                      title="Hapus tahapan"
                      data-testid={`delete-phase-button-${g.id}`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Backlog & Delivery */}
      <Card className="p-5" data-testid="project-backlog-delivery-card">
        <div className="mb-3 flex items-center gap-2">
          <LayoutList size={15} className="text-slate-400" />
          <h3 className="font-heading text-sm font-semibold text-slate-200">Backlog &amp; Delivery</h3>
        </div>

        <div className="mb-2 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Backlog Belum Dikerjakan ({backlogTasks.length})
          </p>
          {canEditProject && backlogTasks.length > 0 && (
            <div className="flex items-center gap-2">
              <Select className="w-48" value={targetSprint} onChange={(e) => setTargetSprint(e.target.value)} data-testid="deliver-target-sprint">
                <option value="">Pilih Sprint Tujuan</option>
                {meta.sprints.filter((s) => s.status !== "completed").map((s) => (
                  <option key={s.id} value={s.id}>{s.name}{s.status === "active" ? " (aktif)" : ""}</option>
                ))}
              </Select>
              <Button
                size="sm"
                onClick={deliverToSprint}
                disabled={selectedBacklog.length === 0 || !targetSprint || delivering}
                data-testid="deliver-to-sprint-button"
              >
                <ArrowRightCircle size={14} /> {delivering ? "Memproses..." : `Kirim ke To Do (${selectedBacklog.length})`}
              </Button>
            </div>
          )}
        </div>

        {backlogTasks.length === 0 ? (
          <p className="mb-4 text-sm text-slate-500">Semua backlog sudah masuk To Do / sedang dikerjakan.</p>
        ) : (
          <div className="mb-4 overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/60 text-left text-xs uppercase tracking-wider text-slate-500">
                  {canEditProject && (
                    <th className="w-10 px-3 py-2">
                      <input type="checkbox" className="sf-check" checked={allBacklogSelected} onChange={toggleAllBacklog} data-testid="deliver-select-all" />
                    </th>
                  )}
                  <th className="px-3 py-2">Task</th>
                  <th className="px-3 py-2">Tahapan</th>
                  <th className="px-3 py-2">PIC</th>
                  <th className="px-3 py-2">Priority</th>
                  <th className="px-3 py-2">Bobot</th>
                  <th className="px-3 py-2">Deadline</th>
                </tr>
              </thead>
              <tbody>
                {pgBacklog.paged.map((t) => (
                  <tr
                    key={t.id}
                    className={`cursor-pointer border-b border-slate-800/60 last:border-0 ${selectedBacklog.includes(t.id) ? "bg-slate-500/5" : ""}`}
                    onClick={() => canEditProject && toggleBacklogTask(t.id)}
                    data-testid={`backlog-delivery-row-${t.id}`}
                  >
                    {canEditProject && (
                      <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                        <input type="checkbox" className="sf-check" checked={selectedBacklog.includes(t.id)} onChange={() => toggleBacklogTask(t.id)} data-testid={`backlog-delivery-checkbox-${t.id}`} />
                      </td>
                    )}
                    <td
                      className="px-3 py-2 font-medium text-slate-200 hover:text-slate-400"
                      onClick={(e) => { e.stopPropagation(); setModal({ open: true, mode: "view", task: t }); }}
                    >
                      {t.title}
                    </td>
                    <td className="px-3 py-2 text-slate-400">{t.phase?.name || "—"}</td>
                    <td className="px-3 py-2">
                      {t.assignee ? (
                        <span className="inline-flex items-center gap-1.5 text-slate-300">
                          <Avatar src={t.assignee.avatar_url} name={t.assignee.name} size={20} />
                          {t.assignee.name}
                        </span>
                      ) : <span className="text-slate-600">Belum ditugaskan</span>}
                    </td>
                    <td className="px-3 py-2"><PriorityBadge priority={t.priority} /></td>
                    <td className="px-3 py-2"><WeightBadge weight={t.weight} /></td>
                    <td className="px-3 py-2">{t.deadline ? <TimelineBadge date={t.deadline} /> : <span className="text-slate-600">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={pgBacklog.page} totalPages={pgBacklog.totalPages} total={pgBacklog.total} perPage={pgBacklog.perPage} onChange={pgBacklog.setPage} />
          </div>
        )}

        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
          Sudah Delivered / Dikerjakan ({deliveredTasks.length})
        </p>
        {deliveredTasks.length === 0 ? (
          <p className="text-sm text-slate-500">Belum ada backlog yang dikirim ke To Do.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-800">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 bg-slate-950/60 text-left text-xs uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">Task</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Sprint</th>
                  <th className="px-3 py-2">PIC</th>
                  <th className="px-3 py-2">Deadline</th>
                </tr>
              </thead>
              <tbody>
                {pgDelivered.paged.map((t) => (
                  <tr
                    key={t.id}
                    className="cursor-pointer border-b border-slate-800/60 last:border-0 hover:bg-white/[0.02]"
                    onClick={() => setModal({ open: true, mode: "view", task: t })}
                    data-testid={`delivered-row-${t.id}`}
                  >
                    <td className="px-3 py-2 font-medium text-slate-200 hover:text-slate-400">{t.title}</td>
                    <td className="px-3 py-2"><StatusBadge status={t.status} /></td>
                    <td className="px-3 py-2 text-slate-400">{t.sprint?.name || "—"}</td>
                    <td className="px-3 py-2">
                      {t.assignee ? (
                        <span className="inline-flex items-center gap-1.5 text-slate-300">
                          <Avatar src={t.assignee.avatar_url} name={t.assignee.name} size={20} />
                          {t.assignee.name}
                        </span>
                      ) : <span className="text-slate-600">Belum ditugaskan</span>}
                    </td>
                    <td className="px-3 py-2">{t.deadline ? <TimelineBadge date={t.deadline} /> : <span className="text-slate-600">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination page={pgDelivered.page} totalPages={pgDelivered.totalPages} total={pgDelivered.total} perPage={pgDelivered.perPage} onChange={pgDelivered.setPage} />
          </div>
        )}
      </Card>

      {/* Gantt timeline per backlog, grouped by phase */}
      {allVisibleTasks.length === 0 ? (
        <EmptyState icon={GanttChartSquare} title="Belum ada backlog" subtitle="Tambahkan backlog untuk melihat timeline project ini." />
      ) : (
        <Card className="p-0" data-testid="project-gantt-card">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-800 px-5 py-3">
            <h3 className="font-heading text-sm font-semibold text-slate-200">Timeline Backlog</h3>
            <div className="flex items-center gap-3">
              <div className="flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                {ganttMode === "workflow" ? (
                  <>
                    {STAT_ORDER.map((s) => (
                      <span key={s} className="flex items-center gap-1.5">
                        <span className="h-2.5 w-4 rounded-full" style={{ backgroundColor: STATUS_META[s]?.color }} /> {STATUS_META[s]?.label}
                      </span>
                    ))}
                  </>
                ) : (
                  <>
                    <span className="flex items-center gap-1.5">
                      <span className="h-2.5 w-4 rounded-full" style={{ backgroundColor: TIMELINE_STATUS_META.on_track.color }} /> On Track
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="h-2.5 w-4 rounded-full" style={{ backgroundColor: TIMELINE_STATUS_META.late.color }} /> Terlambat
                    </span>
                  </>
                )}
              </div>
              <div className="flex overflow-hidden rounded-md border border-white/10">
                {[["workflow", "Workflow"], ["timeline", "Timeline Status"]].map(([m, label]) => (
                  <button
                    key={m}
                    onClick={() => setGanttMode(m)}
                    className={`px-2.5 py-1.5 text-xs font-medium transition-colors ${
                      ganttMode === m ? "bg-[#64748B] text-white" : "bg-[#0F172A] text-slate-400 hover:text-slate-200"
                    }`}
                    data-testid={`gantt-mode-${m}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <GanttTable
              groups={groups}
              days={days}
              months={months}
              rangeStart={rangeStart}
              today={today}
              colorMode={ganttMode}
              onTaskClick={(t) => setModal({ open: true, mode: "view", task: t })}
            />
          </div>
        </Card>
      )}

      <TaskModal
        open={modal.open}
        mode={modal.mode}
        task={modal.task}
        meta={meta}
        onClose={() => setModal({ open: false, mode: "view", task: null })}
        onSaved={load}
      />

      {/* Phase create/edit modal */}
      <PhaseModal
        open={phaseModal.open}
        phase={phaseModal.phase}
        projectId={project.id}
        meta={meta}
        onClose={() => setPhaseModal({ open: false, phase: null })}
        onSaved={load}
      />

      {/* Delete phase confirmation */}
      <Modal
        open={!!deletePhaseTarget}
        onClose={() => setDeletePhaseTarget(null)}
        title="Hapus Tahapan"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeletePhaseTarget(null)}>Batal</Button>
            <Button variant="danger" onClick={confirmDeletePhase} data-testid="confirm-delete-phase">Ya, Hapus</Button>
          </>
        }
      >
        <p className="text-sm text-slate-300">
          Yakin ingin menghapus tahapan <strong>{deletePhaseTarget?.name}</strong>?
          {deletePhaseTarget?.tasks?.length > 0 && (
            <span className="mt-1 block text-rose-400">
              Tahapan ini memiliki {deletePhaseTarget.tasks.length} backlog. Semua backlog di dalamnya akan ikut terhapus permanen.
            </span>
          )}
        </p>
      </Modal>
    </div>
  );
}

// Gabungkan task dengan judul yang sama dalam satu tahapan menjadi satu row
// timeline (multi-PIC / multi-tanggal). Setiap task tetap punya bar sendiri
// di dalam row (lihat GanttTable). Bobot dijumlahkan, tanggal di-union,
// status/warna mengikuti task yang paling "terbelakang".
const STATUS_PROGRESS = { backlog: 0, planned: 0, to_do: 1, in_progress: 2, review: 3, on_hold: 4, done: 5, cancelled: 6, replaced: 6 };

function mergeGanttTasks(tasks) {
  const seen = new Map();
  const out = [];
  for (const t of tasks) {
    const key = (t.title || "").trim().toLowerCase();
    const row = seen.get(key);
    if (!row) {
      const fresh = { ...t, tasks: [t], assignees: t.assignee ? [t.assignee] : [] };
      seen.set(key, fresh);
      out.push(fresh);
      continue;
    }
    row.tasks.push(t);
    if (t.assignee && !row.assignees.some((a) => a.id === t.assignee.id)) row.assignees.push(t.assignee);
    row.weight = (Number(row.weight) || 0) + (Number(t.weight) || 0);
    if (t.start_date && (!row.start_date || t.start_date < row.start_date)) row.start_date = t.start_date;
    if (t.deadline && (!row.deadline || t.deadline > row.deadline)) row.deadline = t.deadline;
    if (t.timeline_status === "late") row.timeline_status = "late";
    if ((STATUS_PROGRESS[t.status] ?? 5) < (STATUS_PROGRESS[row.status] ?? 5)) row.status = t.status;
    row.merged = true;
  }
  return out;
}

function readableTextColor(hex) {
  const c = (hex || "").replace("#", "");
  if (c.length !== 6) return "#F8FAFC";
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.6 ? "#0F172A" : "#F8FAFC";
}

function GanttTable({ groups, days, months, rangeStart, today, colorMode = "workflow", onTaskClick }) {
  return (
    <table className="border-collapse text-sm" style={{ tableLayout: "fixed" }}>
      <thead>
        <tr>
          <th
            rowSpan={2}
            className="sticky left-0 z-30 bg-[#0F172A] px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-400"
            style={{ width: PHASE_COL_WIDTH, minWidth: PHASE_COL_WIDTH }}
          >
            Tahapan
          </th>
          <th
            rowSpan={2}
            className="sticky z-30 bg-[#0F172A] px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-slate-400"
            style={{ left: PHASE_COL_WIDTH, width: TASK_COL_WIDTH, minWidth: TASK_COL_WIDTH }}
          >
            Pekerjaan
          </th>
          {months.map((m, idx) => (
            <th key={idx} colSpan={m.count} className="border-l border-dashed border-white/15 py-1 text-center text-[11px] font-medium text-slate-400">
              {m.label}
            </th>
          ))}
        </tr>
        <tr>
          {days.map((d, idx) => {
            const isToday = daysBetween(today, d) === 0;
            const isWeekend = d.getDay() === 0 || d.getDay() === 6;
            return (
              <th
                key={idx}
                className={`border-l border-dashed border-white/15 text-center text-[10px] font-normal ${isToday ? "text-[#E0B70B]" : isWeekend ? "bg-rose-500/10 font-semibold text-rose-400" : "text-slate-500"}`}
                style={{ width: DAY_WIDTH, minWidth: DAY_WIDTH }}
              >
                {d.getDate()}
                {isToday && <div className="mx-auto mt-0.5 h-1 w-1 rounded-full bg-[#E0B70B]" />}
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {groups.map((group) => {
          const rows = mergeGanttTasks(group.tasks);
          return rows.map((task, i) => {
            const subTasks = task.tasks || [task];
            // Kelompokkan task menjadi bar per rentang tanggal (start+deadline sama
            // → satu bar dengan avatar bertumpuk). Bar sejajar dalam satu garis.
            const barGroups = [];
            const seenBar = new Map();
            for (const st of subTasks) {
              const k = `${st.start_date || ""}|${st.deadline || ""}`;
              const g = seenBar.get(k);
              if (!g) {
                seenBar.set(k, { tasks: [st], assignees: st.assignee ? [st.assignee] : [], weight: Number(st.weight) || 0, start_date: st.start_date, deadline: st.deadline, status: st.status, timeline_status: st.timeline_status });
                barGroups.push(seenBar.get(k));
              } else {
                g.tasks.push(st);
                if (st.assignee && !g.assignees.some((a) => a.id === st.assignee.id)) g.assignees.push(st.assignee);
                g.weight += Number(st.weight) || 0;
                if (st.timeline_status === "late") g.timeline_status = "late";
                if ((STATUS_PROGRESS[st.status] ?? 5) < (STATUS_PROGRESS[g.status] ?? 5)) g.status = st.status;
              }
            }
            return (
            <tr key={task.id} data-testid={`project-gantt-row-${task.id}`}>
              {i === 0 && (
                <td
                  rowSpan={rows.length}
                  className="sticky left-0 z-20 px-2 py-2 text-center align-middle text-xs font-semibold"
                  style={{ width: PHASE_COL_WIDTH, minWidth: PHASE_COL_WIDTH, backgroundColor: group.color, color: readableTextColor(group.color) }}
                >
                  {group.name}
                </td>
              )}
              <td
                className="sticky z-20 cursor-pointer bg-[#0F172A] px-2 py-1.5 text-xs text-slate-200 hover:bg-slate-800"
                style={{ left: PHASE_COL_WIDTH, width: TASK_COL_WIDTH, minWidth: TASK_COL_WIDTH }}
                onClick={() => onTaskClick(task.tasks?.[0] || task)}
                title={task.title}
              >
                <div className="truncate font-medium">{task.title}</div>
              </td>
              {days.map((d, idx) => {
                const isToday = daysBetween(today, d) === 0;
                const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                return (
                  <td
                    key={idx}
                    className={`relative cursor-pointer border-l border-dashed ${isToday ? "border-[#E0B70B]/40" : isWeekend ? "border-rose-500/25 bg-rose-500/[0.05]" : "border-white/15"}`}
                    style={{ width: DAY_WIDTH, minWidth: DAY_WIDTH, height: 44 }}
                    onClick={() => onTaskClick(task.tasks?.[0] || task)}
                  >
                    {barGroups.map((bg, li) => {
                      const start = parseDate(bg.start_date);
                      const end = parseDate(bg.deadline);
                      const tlMeta = TIMELINE_STATUS_META[bg.timeline_status || "on_track"];
                      const color = colorMode === "timeline"
                        ? (tlMeta?.color || "#94A3B8")
                        : (STATUS_META[bg.status]?.color || "#94A3B8");
                      const statusLabel = colorMode === "timeline" ? tlMeta?.label : STATUS_META[bg.status]?.label;
                      const startIdx = start ? Math.max(0, daysBetween(rangeStart, start)) : -1;
                      const endIdx = end ? Math.min(days.length - 1, daysBetween(rangeStart, end)) : startIdx;
                      if (startIdx < 0 || idx !== startIdx) return null;
                      const span = Math.max(1, endIdx - startIdx + 1);
                      const textColor = readableTextColor(color);
                      const names = bg.assignees.map((a) => a.name).join(", ");
                      return (
                        <div
                          key={bg.tasks[0].id}
                          className="absolute flex items-center gap-1.5 overflow-hidden rounded-full px-2"
                          style={{
                            top: 6,
                            left: 2,
                            height: 32,
                            width: Math.max(24, span * DAY_WIDTH - 4),
                            backgroundColor: color,
                            zIndex: 10 + li,
                            boxShadow: "0 1px 4px rgba(0,0,0,.35)",
                            whiteSpace: "nowrap",
                          }}
                          title={`${task.title}: ${statusLabel}${names ? ` — ${names}` : ""}`}
                          data-testid={`gantt-task-bar-${bg.tasks[0].id}`}
                          onClick={(e) => { e.stopPropagation(); onTaskClick(bg.tasks[0]); }}
                        >
                          <div className="flex shrink-0 -space-x-1.5">
                            {bg.assignees.slice(0, 3).map((a, i) =>
                              a?.avatar_url ? (
                                <img
                                  key={a.id ?? i}
                                  src={a.avatar_url}
                                  alt={a.name}
                                  title={a.name}
                                  className="h-6 w-6 rounded-full object-cover ring-1 ring-[#0F172A]"
                                />
                              ) : (
                                <span
                                  key={a?.id ?? i}
                                  title={a?.name}
                                  className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold ring-1 ring-[#0F172A]"
                                  style={{ backgroundColor: textColor === "#F8FAFC" ? "rgba(255,255,255,.25)" : "rgba(15,23,42,.15)", color: textColor }}
                                >
                                  {a?.name?.charAt(0)?.toUpperCase() || "?"}
                                </span>
                              )
                            )}
                            {bg.assignees.length > 3 && (
                              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-700 text-[9px] font-bold text-white ring-1 ring-[#0F172A]">
                                +{bg.assignees.length - 3}
                              </span>
                            )}
                          </div>
                          <span className="ml-auto text-[11px] font-bold" style={{ color: textColor }}>
                            {bg.weight || ""}
                          </span>
                        </div>
                      );
                    })}
                  </td>
                );
              })}
            </tr>
            );
          });
        })}
      </tbody>
    </table>
  );
}
